<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import {
  executeMaintenance,
  getMaintenanceOperation,
  getMaintenanceRereadShifts,
  getMaintenanceShifts,
  previewMaintenance,
  retryMaintenanceBackfill,
  resumeMaintenance,
} from '../../api'
import { useI18n } from '../../i18n'
import { ACTIVE_MAINTENANCE_STATUSES, collectMaintenanceShifts, formatStorageBytes, maintenanceDatasetLabel, operationPercentage } from './maintenanceUi'

const props = defineProps({
  stationId: { type: [String, Number], required: true },
  overview: { type: Object, required: true },
  allowExecute: { type: Boolean, default: false },
  allowResume: { type: Boolean, default: false },
})
const emit = defineEmits(['changed'])
const { locale, t } = useI18n()

const mode = ref('PURGE_THROUGH_SESSION')
const shifts = ref([])
const selectedSession = ref('')
const preview = ref(null)
const operation = ref(null)
const backupAcknowledged = ref(false)
const confirmationText = ref('')
const loadingShifts = ref(false)
const previewing = ref(false)
const executing = ref(false)
const resuming = ref(false)
const retrying = ref(false)
const error = ref('')
const notice = ref('')
const previewHeading = ref(null)
const errorNotice = ref(null)
const operationHeading = ref(null)
let pollTimer = 0
let shiftLoadToken = 0

const selectedShift = computed(() => shifts.value.find((item) => String(item.sessionNumber) === selectedSession.value) ?? null)
const selectedOpenBoundary = computed(() => mode.value === 'PURGE_THROUGH_SESSION'
  && selectedShift.value?.state === 'OPEN')
const blockingAnomalies = computed(() => preview.value?.anomalies?.filter((item) => item.blocksExecution && item.rowCount > 0) ?? [])
const canExecute = computed(() => preview.value
  && props.allowExecute
  && blockingAnomalies.value.length === 0
  && backupAcknowledged.value
  && confirmationText.value === preview.value.confirmationText
  && !executing.value)
const progressPercent = computed(() => operationPercentage(operation.value))
const overallStatus = computed(() => operation.value?.overallStatus ?? operation.value?.status)
const operationActive = computed(() => ACTIVE_MAINTENANCE_STATUSES.has(overallStatus.value))
const operationComplete = computed(() => overallStatus.value === 'SUCCEEDED')
const cleanupActive = computed(() => operation.value?.overallPhase !== 'BACKFILL'
  && ACTIVE_MAINTENANCE_STATUSES.has(operation.value?.status))
const cleanupQueued = computed(() => cleanupActive.value && operation.value?.status === 'QUEUED')

const categoryNames = computed(() => ({
  TRANSACTIONS: t('maintenanceTransactions'),
  RECEIPTS: t('maintenanceReceipts'),
  OBSERVATIONS: t('maintenanceObservations'),
  REFERENCE: t('maintenanceReferenceData'),
}))

function resetFlow() {
  preview.value = null
  operation.value = null
  selectedSession.value = ''
  backupAcknowledged.value = false
  confirmationText.value = ''
  error.value = ''
  notice.value = ''
  stopPolling()
}

function resetPreview() {
  preview.value = null
  operation.value = null
  backupAcknowledged.value = false
  confirmationText.value = ''
  error.value = ''
  notice.value = ''
  stopPolling()
}

async function focusError() {
  await nextTick()
  errorNotice.value?.focus()
}

async function focusOperation() {
  await nextTick()
  operationHeading.value?.focus()
}

async function loadShifts() {
  const requestToken = ++shiftLoadToken
  const stationId = props.stationId
  const selectedMode = mode.value
  if (!stationId || selectedMode === 'FULL_RESET') {
    shifts.value = []
    loadingShifts.value = false
    return
  }
  loadingShifts.value = true
  error.value = ''
  try {
    const loader = selectedMode === 'REREAD_FROM_SESSION' ? getMaintenanceRereadShifts : getMaintenanceShifts
    const loadedShifts = await collectMaintenanceShifts(loader, stationId)
    if (requestToken !== shiftLoadToken) return
    shifts.value = loadedShifts
    if (!shifts.value.some((item) => String(item.sessionNumber) === selectedSession.value)) selectedSession.value = ''
  } catch (cause) {
    if (requestToken !== shiftLoadToken) return
    error.value = requestError(cause)
    await focusError()
  } finally {
    if (requestToken === shiftLoadToken) loadingShifts.value = false
  }
}

async function createPreview() {
  if (mode.value !== 'FULL_RESET' && !selectedSession.value) {
    error.value = mode.value === 'REREAD_FROM_SESSION'
      ? t('maintenanceChooseSourceShift') : t('maintenanceChooseClosedShift')
    await focusError()
    return
  }
  previewing.value = true
  resetPreview()
  try {
    preview.value = await previewMaintenance(
      props.stationId,
      mode.value,
      mode.value === 'PURGE_THROUGH_SESSION' ? Number(selectedSession.value) : null,
      mode.value === 'REREAD_FROM_SESSION' ? Number(selectedSession.value) : null,
      props.overview.dataVersion,
    )
    await nextTick()
    previewHeading.value?.focus()
  } catch (cause) {
    error.value = requestError(cause)
    if (cause.status === 409) emit('changed')
    await focusError()
  } finally {
    previewing.value = false
  }
}

async function execute() {
  if (!canExecute.value) return
  executing.value = true
  error.value = ''
  try {
    operation.value = await executeMaintenance(
      props.stationId,
      preview.value.operationId,
      preview.value.previewToken,
      confirmationText.value,
      backupAcknowledged.value,
    )
    notice.value = t('maintenanceQueued')
    startPolling()
    emit('changed')
    await focusOperation()
  } catch (cause) {
    error.value = requestError(cause)
    if (cause.status === 409) emit('changed')
    await focusError()
  } finally {
    executing.value = false
  }
}

async function refreshOperation() {
  if (!operation.value?.id) return
  try {
    operation.value = await getMaintenanceOperation(props.stationId, operation.value.id)
    if (!operationActive.value) {
      stopPolling()
      emit('changed')
      if (operationComplete.value) notice.value = t('maintenanceCompleted')
    }
  } catch (cause) {
    error.value = requestError(cause)
    stopPolling()
    await focusError()
  }
}

async function resume() {
  if (!operation.value?.canResume) return
  resuming.value = true
  error.value = ''
  try {
    operation.value = await resumeMaintenance(props.stationId, operation.value.id)
    notice.value = t('maintenanceResumed')
    startPolling()
    await focusOperation()
  } catch (cause) {
    error.value = requestError(cause)
    await focusError()
  } finally {
    resuming.value = false
  }
}

function startPolling() {
  stopPolling()
  if (!operationActive.value) return
  pollTimer = window.setInterval(refreshOperation, 2000)
}

function stopPolling() {
  if (pollTimer) window.clearInterval(pollTimer)
  pollTimer = 0
}

function dateTime(value) {
  return value ? new Intl.DateTimeFormat(locale.value, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value)) : '—'
}

function number(value) {
  return new Intl.NumberFormat(locale.value).format(Number(value ?? 0))
}

function anomalyLabel(code) {
  return ({
    INVALID_OR_INCOMPLETE_SESSION_LINK: t('maintenanceInvalidSessionLinks'),
    OPEN_OR_INVALID_SESSION_IN_RANGE: t('maintenanceInvalidBoundarySessions'),
    CROSS_BOUNDARY_SALE_TAG: t('maintenanceCrossBoundarySales'),
  })[code] ?? code
}

async function retryBackfill() {
  if (!operation.value?.backfill?.canRetry) return
  retrying.value = true
  error.value = ''
  try {
    operation.value = await retryMaintenanceBackfill(props.stationId, operation.value.id)
    notice.value = t('maintenanceBackfillRetried')
    startPolling()
    await focusOperation()
  } catch (cause) {
    error.value = requestError(cause)
    await focusError()
  } finally {
    retrying.value = false
  }
}

function openSessions() {
  return (props.overview.openSessionNumbers ?? []).join(', ')
}

function requestError(cause) {
  const key = ({
    maintenance_state_changed: 'maintenanceErrorStateChanged',
    maintenance_preview_stale: 'maintenanceErrorStateChanged',
    station_busy: 'maintenanceErrorStationBusy',
    sync_in_progress: 'maintenanceErrorStationBusy',
    maintenance_in_progress: 'maintenanceErrorStationBusy',
    maintenance_resume_required: 'maintenanceErrorResumeRequired',
    maintenance_preview_replayed: 'maintenanceErrorPreviewReplayed',
    maintenance_preview_expired: 'maintenanceErrorPreviewExpired',
    maintenance_preview_invalid: 'maintenanceErrorPreviewInvalid',
    maintenance_confirmation_invalid: 'maintenanceErrorConfirmation',
    maintenance_anomalies_block_execution: 'maintenanceErrorAnomalies',
    invalid_maintenance_boundary: 'maintenanceErrorBoundary',
    open_shift_blocks_reset: 'maintenanceErrorOpenShift',
    source_preflight_unavailable: 'maintenanceErrorSourceUnavailable',
    source_sessions_unavailable: 'maintenanceErrorSourceUnavailable',
    maintenance_source_boundary_changed: 'maintenanceErrorSourceBoundaryChanged',
    warehouse_unavailable: 'maintenanceErrorWarehouseUnavailable',
  })[cause?.code]
  return key ? t(key) : cause?.message
}

watch(() => props.stationId, () => {
  resetFlow()
  loadShifts()
}, { immediate: true })

watch(mode, () => {
  resetFlow()
  loadShifts()
})

watch(() => props.overview.activeOperation, (active) => {
  if (!active || preview.value) return
  operation.value = active
  startPolling()
}, { immediate: true })

onBeforeUnmount(() => {
  shiftLoadToken += 1
  stopPolling()
})
</script>

<template>
  <section class="card maintenance-wizard" aria-labelledby="maintenance-wizard-title">
    <header class="maintenance-wizard-header">
      <div>
        <p class="eyebrow">{{ t('maintenanceEyebrow') }}</p>
        <h2 id="maintenance-wizard-title">{{ t('maintenanceWizardTitle') }}</h2>
        <p class="muted">{{ t('maintenanceWizardHint') }}</p>
      </div>
      <span v-if="operationActive" class="sync-badge sync-running">{{ t('maintenanceInProgress') }}</span>
    </header>

    <p v-if="error" ref="errorNotice" class="notice error" role="alert" tabindex="-1">{{ error }}</p>
    <p v-if="notice" class="notice success" role="status">{{ notice }}</p>

    <div class="maintenance-step" :class="{ complete: preview }">
      <div class="maintenance-step-number" aria-hidden="true">1</div>
      <div class="maintenance-step-content">
        <h3>{{ t('maintenanceStepMode') }}</h3>
        <div class="maintenance-mode-options">
          <label :class="{ selected: mode === 'PURGE_THROUGH_SESSION' }">
            <input v-model="mode" type="radio" value="PURGE_THROUGH_SESSION" :disabled="operationActive">
            <span><strong>{{ t('maintenancePurgeMode') }}</strong><small>{{ t('maintenancePurgeModeHint') }}</small></span>
            <em>{{ t('recommended') }}</em>
          </label>
          <label :class="{ selected: mode === 'REREAD_FROM_SESSION' }">
            <input v-model="mode" type="radio" value="REREAD_FROM_SESSION" :disabled="operationActive">
            <span><strong>{{ t('maintenanceRereadMode') }}</strong><small>{{ t('maintenanceRereadModeHint') }}</small></span>
          </label>
          <label class="danger-option" :class="{ selected: mode === 'FULL_RESET' }">
            <input v-model="mode" type="radio" value="FULL_RESET" :disabled="operationActive">
            <span><strong>{{ t('maintenanceResetMode') }}</strong><small>{{ t('maintenanceResetModeHint') }}</small></span>
          </label>
        </div>
      </div>
    </div>

    <div class="maintenance-step" :class="{ complete: preview }">
      <div class="maintenance-step-number" aria-hidden="true">2</div>
      <div class="maintenance-step-content">
        <h3>{{ mode === 'FULL_RESET' ? t('maintenanceStepPreviewReset')
          : mode === 'REREAD_FROM_SESSION' ? t('maintenanceStepRereadBoundary') : t('maintenanceStepBoundary') }}</h3>
        <template v-if="mode !== 'FULL_RESET'">
          <div class="maintenance-shift-selector">
            <label>{{ mode === 'REREAD_FROM_SESSION' ? t('maintenanceSourceShift') : t('maintenanceClosedShift') }}
              <select v-model="selectedSession" :disabled="loadingShifts || operationActive" @change="resetPreview">
                <option value="">{{ loadingShifts ? t('loading') : mode === 'REREAD_FROM_SESSION'
                  ? t('maintenanceChooseSourceShift') : t('maintenanceChooseClosedShift') }}</option>
                <option v-for="shift in shifts" :key="shift.sessionNumber" :value="String(shift.sessionNumber)">
                  №{{ shift.sessionNumber }} · {{ shift.state === 'OPEN' ? t('maintenanceShiftOpen') : dateTime(shift.closedAt) }} · {{ shift.operatorName || '—' }}
                </option>
              </select>
            </label>
          </div>
          <p v-if="selectedShift" class="maintenance-boundary-summary">
            {{ mode === 'REREAD_FROM_SESSION'
              ? t('maintenanceRereadBoundarySummary', { session: selectedShift.sessionNumber })
              : selectedOpenBoundary
                ? t('maintenanceOpenBoundarySummary', { session: selectedShift.sessionNumber })
              : t('maintenanceBoundarySummary', { session: selectedShift.sessionNumber, closedAt: dateTime(selectedShift.closedAt) }) }}
          </p>
          <p v-if="mode === 'REREAD_FROM_SESSION'" class="notice info">{{ t('maintenanceRereadImpact') }}</p>
        </template>
        <template v-else>
          <p class="notice info">{{ overview.openSessionNumbers?.length
            ? t('maintenanceCurrentShiftWarning', { sessions: openSessions() })
            : t('maintenanceResetImpact') }}</p>
        </template>
        <button class="button" type="button" :disabled="previewing || operationActive || (mode !== 'FULL_RESET' && !selectedSession)" @click="createPreview">
          {{ previewing ? t('maintenancePreviewing') : t('maintenancePreviewAction') }}
        </button>
      </div>
    </div>

    <section v-if="preview" class="maintenance-preview" aria-labelledby="maintenance-preview-title">
      <header>
        <div>
          <p class="eyebrow">{{ t('maintenancePreviewEyebrow') }}</p>
          <h3 id="maintenance-preview-title" ref="previewHeading" tabindex="-1">{{ mode === 'REREAD_FROM_SESSION' ? t('maintenanceRereadPreviewTitle') : t('maintenancePreviewTitle') }}</h3>
        </div>
        <strong>{{ formatStorageBytes(preview.estimatedReusableBytes) }}</strong>
      </header>
      <p class="muted">{{ t('maintenanceReusableCaveat') }}</p>
      <p v-if="preview.recentBoundaryWarning" class="notice warning">{{ t('maintenanceRecentShiftWarning') }}</p>
      <div v-if="preview.anomalies.length" class="maintenance-anomalies">
        <h4>{{ t('maintenanceAnomalies') }}</h4>
        <p v-for="anomaly in preview.anomalies" :key="anomaly.code" :class="['notice', anomaly.blocksExecution ? 'error' : 'warning']">
          {{ anomalyLabel(anomaly.code) }}: {{ number(anomaly.rowCount) }}
        </p>
      </div>
      <div class="table-wrap maintenance-preview-table"><table><thead><tr><th>{{ t('maintenanceCategory') }}</th><th>{{ t('maintenanceDataset') }}</th><th>{{ t('maintenanceRowsToDelete') }}</th><th>{{ t('maintenanceReusableSpace') }}</th></tr></thead><tbody>
        <tr v-for="item in preview.estimates" :key="item.tableKey"><td>{{ categoryNames[item.category] ?? item.category }}</td><td><span class="maintenance-preview-dataset-name">{{ maintenanceDatasetLabel(item.tableKey, t) }}</span><code>{{ item.tableKey }}</code></td><td>{{ number(item.rowCount) }}</td><td>{{ formatStorageBytes(item.estimatedReusableBytes) }}</td></tr>
      </tbody></table></div>
    </section>

    <div v-if="preview && !operation && allowExecute" class="maintenance-step danger-step">
      <div class="maintenance-step-number" aria-hidden="true">3</div>
      <div class="maintenance-step-content">
        <h3>{{ t('maintenanceStepConfirm') }}</h3>
        <label class="checkbox maintenance-backup-ack">
          <input v-model="backupAcknowledged" type="checkbox">
          <span>{{ t('maintenanceBackupUnknownAck') }}</span>
        </label>
        <label>{{ t('maintenanceConfirmationLabel') }}
          <code class="maintenance-confirmation-phrase">{{ preview.confirmationText }}</code>
          <input v-model="confirmationText" type="text" autocomplete="off" :placeholder="t('maintenanceConfirmationPlaceholder')">
        </label>
        <p v-if="blockingAnomalies.length" class="notice error" role="alert">
          {{ t('maintenanceBlockingAnomalyHint') }}
        </p>
        <button class="button button-danger" type="button" :disabled="!canExecute" @click="execute">
          {{ executing ? t('maintenanceStarting') : mode === 'REREAD_FROM_SESSION'
            ? t('maintenanceExecuteRereadAction') : t('maintenanceExecuteAction') }}
        </button>
      </div>
    </div>

    <section v-if="operation" class="maintenance-operation" aria-live="polite">
      <header class="section-heading">
        <div><p class="eyebrow">{{ t('maintenanceOperationEyebrow') }}</p><h3 ref="operationHeading" tabindex="-1">{{ t('maintenanceOperationTitle', { id: operation.id }) }}</h3></div>
        <span class="sync-badge" :class="operationComplete ? 'sync-succeeded' : operation.canResume || operation.backfill?.canRetry ? 'sync-failed' : 'sync-running'">{{ overallStatus }}</span>
      </header>
      <p v-if="operation.overallPhase === 'BACKFILL'" class="notice info">{{ t('maintenanceBackfillInProgress') }}</p>
      <div v-if="operation.overallPhase !== 'BACKFILL'" class="maintenance-progress-summary">
        <progress v-if="cleanupQueued"></progress>
        <progress v-else :value="progressPercent" max="100">{{ progressPercent }}%</progress>
        <strong>{{ cleanupQueued ? t('maintenanceWaitingToStart') : `${progressPercent}%` }}</strong>
      </div>
      <div v-if="operation.overallPhase !== 'BACKFILL'" class="maintenance-progress-list">
        <article v-for="item in operation.progress" :key="item.tableKey">
          <div><strong><code>{{ item.tableKey }}</code></strong><span>{{ number(item.deletedRows) }} / {{ number(item.plannedRows) }}</span></div>
          <progress :value="item.percentage" max="100">{{ Math.round(item.percentage) }}%</progress>
        </article>
      </div>
      <section v-if="operation.backfill" class="maintenance-backfill-progress">
        <div class="maintenance-backfill-heading">
          <strong>{{ t('maintenanceBackfillTitle') }}</strong>
          <span>{{ operation.backfill.status }}</span>
        </div>
        <progress v-if="operation.backfill.percentage != null" :value="operation.backfill.percentage" max="100"></progress>
        <progress v-else-if="operation.backfill.status === 'QUEUED' || operation.backfill.status === 'RUNNING'"></progress>
        <dl>
          <div><dt>{{ t('maintenanceRowsRead') }}</dt><dd>{{ number(operation.backfill.rowsSeen) }}</dd></div>
          <div><dt>{{ t('maintenanceRowsChanged') }}</dt><dd>{{ number(operation.backfill.rowsChanged) }}</dd></div>
          <div><dt>{{ t('maintenanceCurrentDataset') }}</dt><dd><code>{{ operation.backfill.currentTable || '—' }}</code></dd></div>
          <div><dt>{{ t('maintenanceElapsed') }}</dt><dd>{{ number(operation.backfill.elapsedSeconds) }} {{ t('secondsShort') }}</dd></div>
        </dl>
        <p v-if="operation.backfill.error" class="notice error">{{ operation.backfill.error.message }}</p>
      </section>
      <p v-if="operation.error" class="notice error">{{ operation.error.message }} <small>{{ operation.error.correlationId }}</small></p>
      <button v-if="operation.canResume && allowResume" class="button" type="button" :disabled="resuming" @click="resume">{{ resuming ? t('maintenanceResuming') : t('maintenanceResumeAction') }}</button>
      <button v-if="operation.backfill?.canRetry && allowResume" class="button" type="button" :disabled="retrying" @click="retryBackfill">{{ retrying ? t('maintenanceRetryingBackfill') : t('maintenanceRetryBackfill') }}</button>
      <p v-if="operationComplete" class="notice success">{{ t('maintenanceCompletedManifest', { count: number(operation.actualCounts?.reduce((total, item) => total + item.deletedRowCount, 0) ?? 0) }) }}</p>
    </section>
  </section>
</template>
